/**
 * §17 Integrations "GSP and gateway configuration" — which payment gateway, GSP (e-invoice) adapter
 * and e-way bill adapter the next payment, registration and dispatch will use, what is registered to
 * switch to, and (for `integrations.administer`) the credentials behind them. Billing lays what is
 * saved here over `appsettings` and reads it live at the point of use, so a change needs no release
 * (FR-004) and no restart. A secret (PEM key, client secret, password) is write-only: the server
 * never sends it back, only whether one is set, so a blank secret field means "keep what is saved".
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/integration-settings.tsx
 *   the page: one bordered row per integration, name and description beside its state;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/horiozontal-block.tsx
 *   the section block (`HorizontalFormBlockWrapper`).
 *
 * The screen-to-template mapping is specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useLoaderData, useOutletContext, useRevalidator } from "react-router-dom";
import { Badge, Button, Input, Password, Text, Textarea, Title } from "rizzui";
import { CaveatBanner, ErrorSurface, HorizontalFormBlockWrapper, PageHeader } from "@steelyard/theme";
import { useConfirm } from "../../components/confirm-modal";
import { SelectField } from "../../components/select-field";
import { ADDRESS_STATES } from "../../lib/invoice-model";
import { can } from "../../lib/permissions";
import { getIntegrationSettings, saveIntegrationSettings, type IntegrationSettings } from "../../lib/services/invoicing";
import type { AccountSummary, PermissionState } from "../../types";

export function integrationSettingsLoader() {
  return getIntegrationSettings();
}

function Integration({ name, content, active, available }: { name: string; content: string; active: string; available: readonly string[] }) {
  const registered = available.some((option) => option.toLowerCase() === active.toLowerCase());
  return (
    <div className="col-span-2 flex gap-3 rounded-lg border border-muted p-6">
      <div className="flex flex-grow flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Title as="h3" className="mb-1 text-base font-semibold">
            {name}
          </Title>
          <Text className="text-sm text-gray-500">{content}</Text>
          <Text className="mt-3 text-xs text-gray-500">Registered: {available.length ? available.join(", ") : "none"}</Text>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">{active}</Badge>
          {active.toLowerCase() === "stub" ? <Badge color="warning">Stub — nothing real is sent</Badge> : null}
          {registered ? null : <Badge color="danger">Not registered</Badge>}
        </div>
      </div>
    </div>
  );
}

type Spec = { key: string; label: string; hint?: string; placeholder?: string };

const GROUPS: { title: string; description: string; fields: Spec[]; pem: Spec[] }[] = [
  {
    title: "PayGlocal — payment gateway",
    description: "From PayGlocal's Control Center for your merchant. Use the UAT values until PayGlocal has approved production.",
    fields: [
      { key: "PayGlocal:BaseUrl", label: "API base URL", placeholder: "https://api.uat.payglocal.in" },
      { key: "PayGlocal:CallbackBaseUrl", label: "This API's public URL", hint: "Where PayGlocal returns the customer's browser." },
      { key: "PayGlocal:MerchantId", label: "Merchant id (MID)" },
      { key: "PayGlocal:ParentMerchantId", label: "Parent merchant id", hint: "Portfolio merchants only." },
      { key: "PayGlocal:MerchantKeyId", label: "Your key id (kid)" },
      { key: "PayGlocal:PayGlocalKeyId", label: "PayGlocal's key id" },
    ],
    pem: [
      { key: "PayGlocal:MerchantPrivateKeyPem", label: "Your private key (PEM)" },
      { key: "PayGlocal:PayGlocalPublicKeyPem", label: "PayGlocal's public key (PEM)", hint: "A PUBLIC KEY block. If PayGlocal gave you a certificate, extract it: openssl x509 -in file.cert -pubkey -noout" },
      { key: "PayGlocal:WebhookPublicKeyPem", label: "Webhook signing key (PEM)", hint: "Optional — PayGlocal's public key is used when blank." },
    ],
  },
  {
    title: "NIC — e-invoice and e-way bill",
    description: "From the NIC e-invoice and e-way bill API portals (sandbox first). The GSTIN must be registered with NIC for that environment.",
    fields: [
      { key: "Nic:EinvoiceBaseUrl", label: "E-invoice API URL" },
      { key: "Nic:EwayBillBaseUrl", label: "E-way bill API URL" },
      { key: "Nic:Gstin", label: "GSTIN" },
      { key: "Nic:ClientId", label: "Client id" },
      { key: "Nic:UserName", label: "API user name" },
      { key: "Nic:ClientSecret", label: "Client secret" },
      { key: "Nic:Password", label: "API password" },
    ],
    pem: [{ key: "Nic:PublicKeyPem", label: "NIC public key (PEM)", hint: "Downloaded from the NIC portal after login." }],
  },
  {
    title: "Supplier on invoices",
    description: "The registered legal entity printed on every invoice and sent to NIC. Not a placeholder: NIC refuses an invoice without it.",
    fields: [
      { key: "SupplierPlacesOfBusiness:0:LegalName", label: "Legal name" },
      { key: "SupplierPlacesOfBusiness:0:Gstin", label: "GSTIN" },
      { key: "SupplierPlacesOfBusiness:0:StateCode", label: "State", hint: "Must match the GSTIN's state." },
      { key: "SupplierPlacesOfBusiness:0:Address1", label: "Address" },
      { key: "SupplierPlacesOfBusiness:0:Locality", label: "Locality / city" },
      { key: "SupplierPlacesOfBusiness:0:Pin", label: "PIN code" },
    ],
    pem: [],
  },
];

const SELECTORS = ["ActivePaymentProvider", "GspAdapter", "EwayBillAdapter"] as const;

const FOLLOW = { database: "Saved on this screen.", configuration: "From server configuration.", unset: "Not set." } as const;

export default function IntegrationSettingsPage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const revalidator = useRevalidator();
  const data = useLoaderData() as IntegrationSettings;

  if (!can(permissionState, "integrations.read")) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "Integration settings are gated by integrations.read, which your role does not hold." }}
        action="view the integration settings"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title="Gateway & GSP"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Integrations" }, { name: "Gateway & GSP" }]}
      />
      <HorizontalFormBlockWrapper
        title="Gateway and GSP configuration"
        description={
          data.stubsEnabled
            ? "What the next payment, registration and e-way bill will use. This host may run stubs, which take no money and register nothing."
            : "What the next payment, registration and e-way bill will use. Changed in configuration, taking effect without a release."
        }
      >
        <Integration name="Payment gateway" content="Takes and refunds payments (§15)." active={data.activePaymentProvider} available={data.availablePaymentProviders} />
        <Integration name="E-invoice (GSP)" content="Registers invoices and obtains the IRN (§13.3)." active={data.gspAdapter} available={data.availableGspAdapters} />
        <Integration name="E-way bill" content="Generates e-way bills at dispatch (§13.4)." active={data.ewayBillAdapter} available={data.availableEwayBillAdapters} />
      </HorizontalFormBlockWrapper>

      {can(permissionState, "integrations.administer") ? (
        // Remounted on every save so the fields show what the server now holds.
        <SettingsForm key={JSON.stringify(data)} data={data} onSaved={() => revalidator.revalidate()} />
      ) : null}
    </div>
  );
}

function SettingsForm({ data, onSaved }: { data: IntegrationSettings; onSaved: () => void }) {
  const confirm = useConfirm();
  const byKey = new Map(data.fields.map((f) => [f.key, f]));
  const initial: Record<string, string> = {
    ActivePaymentProvider: data.activePaymentProvider,
    GspAdapter: data.gspAdapter,
    EwayBillAdapter: data.ewayBillAdapter,
  };
  // A secret never comes back, so its field starts blank and blank means "keep it".
  for (const f of data.fields) if (!f.secret) initial[f.key] = f.value ?? "";

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<Record<string, string>>({ defaultValues: initial });

  const onSubmit = handleSubmit(async (values) => {
    const changed: Record<string, string> = {};
    for (const [key, raw] of Object.entries(values)) {
      const value = (raw ?? "").trim();
      if (value !== (initial[key] ?? "")) changed[key] = value;
    }
    if (Object.keys(changed).length === 0) return void toast("Nothing to save — no field was changed.");

    const save = async () => {
      try {
        await saveIntegrationSettings(changed);
        toast.success("Saved — in use from the next request, no restart needed.");
        onSaved();
      } catch (err) {
        toast.error((err as { detail?: string; title?: string }).detail ?? (err as { title?: string }).title ?? "The settings were refused.");
      }
    };

    // Moving off the stub, or between real providers, changes what real money and real statutory documents do.
    const live = SELECTORS.filter((key) => key in changed && changed[key] && changed[key].toLowerCase() !== "stub");
    if (live.length === 0) return save();
    confirm({
      title: "Switch to a live provider?",
      consequence: (
        <>
          From the next payment, invoice or dispatch on this server: {live.map((key) => `${key} → ${changed[key]}`).join(", ")}. Real
          payments are taken and real IRNs and e-way bills are issued; a wrong credential makes them fail. The server refuses the
          change while anything the provider needs is still empty.
        </>
      ),
      confirmLabel: "Switch",
      onConfirm: () => void save(),
    });
  });

  const renderField = ({ key, label, hint, placeholder }: Spec, multiline = false) => {
    const field = byKey.get(key);
    const secret = Boolean(field?.secret);
    const note = secret ? (field?.isSet ? "Set — leave blank to keep it." : "Not set.") : FOLLOW[(field?.source ?? "unset") as keyof typeof FOLLOW];
    const help = [hint, note].filter(Boolean).join(" ");
    const common = {
      label,
      helperText: help,
      disabled: secret && !data.secretsCanBeSaved,
      placeholder: secret && field?.isSet ? "••••••••  (set)" : placeholder,
      ...register(key),
    };

    // Picked by name; the two-digit code it stores is what NIC and the GSTIN use.
    if (key.endsWith(":StateCode")) {
      return (
        <SelectField key={key} label={label} hint={help} {...register(key)}>
          <option value="">Choose a state…</option>
          {ADDRESS_STATES.map((state) => (
            <option key={state.code} value={state.code}>
              {state.name}
            </option>
          ))}
        </SelectField>
      );
    }

    return multiline ? (
      <Textarea key={key} className="col-span-full" textareaClassName="font-mono text-xs" rows={4} {...common} />
    ) : secret ? (
      <Password key={key} autoComplete="new-password" {...common} />
    ) : (
      <Input key={key} autoComplete="off" {...common} />
    );
  };

  const adapter = (key: (typeof SELECTORS)[number], label: string, options: string[]) => (
    <SelectField key={key} label={label} {...register(key)}>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </SelectField>
  );

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
      {data.secretsCanBeSaved ? null : (
        <CaveatBanner caveat="Keys and passwords cannot be saved here yet: this server has no Billing:SettingsKey to encrypt them with. Everything else can." />
      )}

      <HorizontalFormBlockWrapper title="Providers in use" description="What the next payment, e-invoice and e-way bill will go through. A provider can only be chosen once everything it needs is filled in below.">
        {adapter("ActivePaymentProvider", "Payment gateway", data.availablePaymentProviders)}
        {adapter("GspAdapter", "E-invoice (GSP)", data.availableGspAdapters)}
        {adapter("EwayBillAdapter", "E-way bill", data.availableEwayBillAdapters)}
      </HorizontalFormBlockWrapper>

      {GROUPS.map((group) => (
        <HorizontalFormBlockWrapper key={group.title} title={group.title} description={group.description}>
          {group.fields.map((spec) => renderField(spec))}
          {group.pem.map((spec) => renderField(spec, true))}
        </HorizontalFormBlockWrapper>
      ))}

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  );
}
